// The publishable-state rules for a TikTok Direct Post, as pure functions.
//
// Kept out of the component on purpose: every one of these is a literal clause
// in TikTok's Content Sharing Guidelines, they are what an audit is checked
// against, and a rule you can unit-test is a rule that cannot quietly regress
// behind a re-render.
//
// https://developers.tiktok.com/doc/content-sharing-guidelines/

// 5c + the Music Usage Confirmation. Three variants, and WHICH one shows is
// itself a compliance requirement — branded content pulls in a second policy.
export const DECLARATION_PLAIN =
  'By posting, you agree to TikTok’s Music Usage Confirmation';
export const DECLARATION_BRANDED =
  'By posting, you agree to TikTok’s Branded Content Policy and Music Usage Confirmation';

export function declarationFor({ commercialContent, yourBrand, brandedContent }) {
  if (!commercialContent) return DECLARATION_PLAIN;
  // "Branded Content" on its own OR together with "Your Brand" → both policies.
  return brandedContent ? DECLARATION_BRANDED : DECLARATION_PLAIN;
}

// 3a. The label the user is told their post will carry.
export function disclosureLabel({ commercialContent, yourBrand, brandedContent }) {
  if (!commercialContent) return '';
  if (brandedContent) return 'Your photo/video will be labeled as ‘Paid partnership’';
  if (yourBrand) return 'Your photo/video will be labeled as ‘Promotional content’';
  return '';
}

// 3b. Branded content cannot be private. Enforced BOTH ways: the option is
// unselectable while SELF_ONLY is chosen, and SELF_ONLY is unselectable once
// branded content is ticked — TikTok accepts either interlock, we do both so
// the user can never reach the invalid combination from either direction.
export const SELF_ONLY = 'SELF_ONLY';
export function brandedContentAllowed(privacyLevel) {
  return privacyLevel !== SELF_ONLY;
}
export function privacyOptionDisabled(option, { commercialContent, brandedContent }) {
  return option === SELF_ONLY && !!commercialContent && !!brandedContent;
}

// Duet and Stitch do not exist for photo posts (2c).
export function interactionsFor(mediaType) {
  return mediaType === 'PHOTO' ? ['comment'] : ['comment', 'duet', 'stitch'];
}

/**
 * Can this be published? Returns '' when yes, else the reason — which doubles
 * as the tooltip, so the disabled state and its explanation can never disagree.
 */
export function blockingReason(state, creator) {
  if (!creator) return 'Loading your TikTok account…';
  // 1b: the creator_info answer is authoritative — stop, do not queue.
  if (creator.canPost === false) {
    return 'TikTok says this account cannot post right now. Please try again later.';
  }
  // 2b: no default. Nothing is selected until the user selects it.
  if (!state.privacyLevel) return 'Choose who can see this post.';
  // 3a: disclosure on but nothing chosen.
  if (state.commercialContent && !state.yourBrand && !state.brandedContent) {
    return 'You need to indicate if your content promotes yourself, a third party, or both.';
  }
  if (state.commercialContent && state.brandedContent && !brandedContentAllowed(state.privacyLevel)) {
    return 'Branded content visibility cannot be set to private.';
  }
  // 1c: a video longer than the creator's ceiling is rejected by TikTok anyway.
  if (state.videoDurationSec && creator.maxVideoPostDurationSec
      && state.videoDurationSec > creator.maxVideoPostDurationSec) {
    return `This video is longer than TikTok allows for this account (${creator.maxVideoPostDurationSec}s).`;
  }
  return '';
}

// What gets persisted onto the post. consentedAt is the load-bearing field:
// the worker treats its presence as express consent and only then uses
// DIRECT_POST — anything without it goes to the creator's inbox as a draft.
export function toPostInfo(state, creator) {
  return {
    privacyLevel:      state.privacyLevel,
    allowComment:      !!state.allowComment,
    allowDuet:         !!state.allowDuet,
    allowStitch:       !!state.allowStitch,
    commercialContent: !!state.commercialContent,
    yourBrand:         !!state.yourBrand,
    brandedContent:    !!state.brandedContent,
    creatorNickname:   (creator && creator.nickname) || '',
    consentedAt:       new Date().toISOString(),
  };
}

export const INITIAL_STATE = {
  privacyLevel: '',        // NO default — 2b is explicit about this
  allowComment: false,     // 2c — all off until deliberately enabled
  allowDuet: false,
  allowStitch: false,
  commercialContent: false, // 3a — disclosure off by default
  yourBrand: false,
  brandedContent: false,
  videoDurationSec: 0,
};
